/**
 * Add API Connection — 4-step wizard
 *
 * Steps:
 * 1. Upload/Paste OpenAPI spec → parse and discover endpoints
 * 2. Configure authentication (type-specific fields)
 * 3. Select which endpoints to expose as tools
 * 4. Review + Name + Test Connection
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
// Codex retest #266 — `useCallback` (react), and `Upload` /
// `CheckSquare` / `Settings2` (lucide) were imported but never
// rendered. Removed to clean the wizard's surface area.
import {
  ArrowLeft, FileJson, Key,
  Eye, EyeOff, Check, X, AlertCircle, Plus, Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
// Codex retest #266 — wizard only uses the bare Card + CardContent
// pair (success banner + summary cards). CardHeader/Title/Description
// were imported speculatively and never rendered.
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { StepWizard } from '@/components/shared/step-wizard';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { METHOD_COLORS, AUTH_LABELS, type AuthType, type HttpMethod, type EndpointCategory } from '@/lib/mock-connections-data';
import { useConnectionsCapabilities, useCreateApiConnection } from '@/lib/use-connections-module';
import { ConnectionsRoleGate } from './-connections-role-gate';

export const Route = createFileRoute('/connections/new-api')({
  component: NewApiPage,
});

// ── Parsed endpoint shape ─────────────────────────────────────────────────
//
// Codex retest #266 — the SAMPLE_PARSED_ENDPOINTS fixture below was
// dead code from the round-1 prototype. Real parsing now happens
// server-side via POST /connections-module/parse-spec, which returns
// the same ParsedEndpoint[] shape, so the local fixture only existed
// to confuse new readers. Deleted.

interface ParsedEndpoint {
  id: string;
  method: HttpMethod;
  path: string;
  operationId: string;
  summary: string;
  category: EndpointCategory;
  selected: boolean;
}

// ── Main Component ─────────────────────────────────────────────────────────

// Exported so the nested-IA alias route at
// /connections/api/new (added round 4 / #228) can re-render the same
// component without duplicating wizard logic.
export function NewApiPage() {
  const navigate = useNavigate();
  const capabilities = useConnectionsCapabilities();

  // Step 1 state
  const [specText, setSpecText] = useState('');
  const [parsed, setParsed] = useState(false);
  const [endpoints, setEndpoints] = useState<ParsedEndpoint[]>([]);
  const [specFormat, setSpecFormat] = useState<'openapi3' | 'openapi2' | 'graphql'>('openapi3');

  // Step 2 state
  const [authType, setAuthType] = useState<AuthType>('api_key');
  const [authApiKeyHeader, setAuthApiKeyHeader] = useState('X-API-Key');
  const [authApiKeyValue, setAuthApiKeyValue] = useState('');
  const [authOauthTokenUrl, setAuthOauthTokenUrl] = useState('');
  const [authOauthClientId, setAuthOauthClientId] = useState('');
  const [authOauthClientSecret, setAuthOauthClientSecret] = useState('');
  const [authOauthScopes, setAuthOauthScopes] = useState('');
  const [authBasicUser, setAuthBasicUser] = useState('');
  const [authBasicPass, setAuthBasicPass] = useState('');
  const [authBearerToken, setAuthBearerToken] = useState('');
  // Codex retest #265 — Custom Headers auth scheme. Functional spec
  // includes `custom_headers` and the backend probe + post-save test
  // already handles it (`else if (authType === 'custom_headers') ...`
  // in connections-module.ts), but the wizard didn't expose it. Each
  // pair has a stable client-side `id` so React can key the rows
  // safely as the admin adds/removes them. The runtime payload is
  // serialized into the same `headers: { name: value }` shape the
  // probe handler reads via `cfg.headers`.
  const [authCustomHeaders, setAuthCustomHeaders] = useState<
    Array<{ id: string; name: string; value: string }>
  >([{ id: 'h-1', name: '', value: '' }]);
  const [showSecret, setShowSecret] = useState(false);

  // Step 4 state
  const [connectionName, setConnectionName] = useState('');
  const [connectionDescription, setConnectionDescription] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [testResult, setTestResult] = useState<'idle' | 'testing' | 'success' | 'failed'>('idle');
  // Codex retest #239 — show the real backend probe message in Step 4
  // (e.g. "Reachable (HTTP 200, 69ms)" / "Probe failed: …") instead of
  // an opaque green check next to a fake-passed test.
  const [testMessage, setTestMessage] = useState<string>('');
  // Codex retest #264 — when the probe is OAuth, the backend returns
  // a 3-step breakdown (Token endpoint reachable / Token issued /
  // Base URL reachable with token). Non-OAuth probes leave this null
  // and the wizard falls back to the round-5 single-message render.
  const [testSteps, setTestSteps] = useState<
    Array<{ name: string; ok: boolean; message: string; latencyMs: number | null }>
    | null
  >(null);

  // Parse handler — real OpenAPI 3 parser via /connections-module/parse-spec
  const handleParse = async () => {
    if (!specText.trim()) {
      toast.error('Please paste an API specification');
      return;
    }
    toast.info('Parsing specification...');
    try {
      // Lazy-import keeps this wizard out of the initial bundle since
      // most users open a connection detail before reaching the spec
      // parser. The earlier round-4 version of this block had a stray
      // `default` import alongside the named one — left over from a
      // refactor — that flunked typecheck because api-client has no
      // default export. Removed; only `apiClient` is needed.
      const { apiClient } = await import('@/lib/api-client');
      const res = await apiClient.post<{
        baseUrl: string | null;
        title: string | null;
        description: string | null;
        endpoints: ParsedEndpoint[];
      }>('/connections-module/parse-spec', { spec: specText, format: specFormat });
      setParsed(true);
      setEndpoints(res.endpoints);
      if (res.title) setConnectionName(res.title);
      if (res.description) setConnectionDescription(res.description);
      if (res.baseUrl) setBaseUrl(res.baseUrl);
      toast.success(`Discovered ${res.endpoints.length} endpoints`);
    } catch (e) {
      toast.error('Parse failed', {
        description: 'Spec must be JSON OpenAPI 3.',
      });
    }
  };

  // Endpoint toggle
  const toggleEndpoint = (id: string) => {
    setEndpoints(prev => prev.map(ep => ep.id === id ? { ...ep, selected: !ep.selected } : ep));
  };

  const selectAll = () => setEndpoints(prev => prev.map(ep => ({ ...ep, selected: true })));
  const deselectAll = () => setEndpoints(prev => prev.map(ep => ({ ...ep, selected: false })));

  const selectedCount = endpoints.filter(ep => ep.selected).length;

  // Codex retest #239 — single source of truth for what gets sent on
  // both Test (probe) and Complete (create). The previous Complete
  // handler dropped every credential except `headerName`, so saved
  // rows had no auth secrets at rest. Both flows now include the same
  // shape, and the backend's auth-test handler reads from these exact
  // keys (apiKey, token, username/password, clientSecret).
  const buildAuthConfig = (): Record<string, unknown> => {
    const cfg: Record<string, unknown> = {};
    if (authType === 'api_key') {
      cfg.headerName = authApiKeyHeader;
      cfg.apiKey = authApiKeyValue;
    } else if (authType === 'oauth2') {
      cfg.tokenUrl = authOauthTokenUrl;
      cfg.clientId = authOauthClientId;
      cfg.clientSecret = authOauthClientSecret;
      cfg.scopes = authOauthScopes
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    } else if (authType === 'basic') {
      cfg.username = authBasicUser;
      cfg.password = authBasicPass;
    } else if (authType === 'bearer') {
      cfg.token = authBearerToken;
    } else if (authType === 'custom_headers') {
      // Codex retest #265 — flatten the wizard's pair list into the
      // `{ headerName: headerValue }` map the backend reads from
      // `cfg.headers`. Empty rows (admin started typing then cleared)
      // are dropped so they don't ship as `'': ''` — that would
      // break upstream HTTP libraries.
      const headerMap: Record<string, string> = {};
      for (const h of authCustomHeaders) {
        const name = h.name.trim();
        const value = h.value.trim();
        if (!name || !value) continue;
        headerMap[name] = value;
      }
      cfg.headers = headerMap;
    }
    return cfg;
  };

  // Codex retest #239 — credential completeness check for Step 2.
  // `none` always passes; every other auth type needs its required
  // secret slot non-empty before the user can proceed. This blocks the
  // "Next enabled with blank API Key" failure noted in the QA report.
  const authConfigured = (() => {
    if (authType === 'none') return true;
    if (authType === 'api_key')
      return !!authApiKeyHeader.trim() && !!authApiKeyValue.trim();
    if (authType === 'bearer') return !!authBearerToken.trim();
    if (authType === 'basic')
      return !!authBasicUser.trim() && !!authBasicPass.trim();
    if (authType === 'oauth2')
      return (
        !!authOauthTokenUrl.trim() &&
        !!authOauthClientId.trim() &&
        !!authOauthClientSecret.trim()
      );
    if (authType === 'custom_headers') {
      // At least one (name, value) pair must be filled. Empty rows
      // are tolerated — admins commonly leave a blank row at the end
      // for the next addition — but Next stays disabled until at
      // least one pair is real.
      return authCustomHeaders.some(
        (h) => h.name.trim().length > 0 && h.value.trim().length > 0,
      );
    }
    return true;
  })();

  // Real probe handler — calls POST /connections-module/api/probe
  // with the exact baseUrl + auth payload the wizard would persist.
  // The backend hits the URL with a 5s timeout and reports a real
  // HTTP status / latency / failure message. Replaces the previous
  // local setTimeout fake-pass.
  const handleTestConnection = async () => {
    if (!baseUrl.trim()) {
      toast.error('Enter a Base URL before testing');
      return;
    }
    setTestResult('testing');
    setTestMessage('');
    setTestSteps(null);
    const t = toast.loading(
      authType === 'oauth2' ? 'Probing OAuth endpoints…' : 'Testing connection…',
    );
    try {
      const { apiClient } = await import('@/lib/api-client');
      const res = await apiClient.post<{
        ok: boolean;
        status: string;
        message: string;
        latencyMs: number | null;
        testedAt: string;
        steps?: Array<{
          name: string;
          ok: boolean;
          message: string;
          latencyMs: number | null;
        }>;
      }>('/connections-module/api/probe', {
        baseUrl,
        authType,
        authConfig: buildAuthConfig(),
      });
      toast.dismiss(t);
      setTestMessage(res.message);
      setTestSteps(res.steps ?? null);
      if (res.ok) {
        setTestResult('success');
        toast.success(res.message);
      } else {
        setTestResult('failed');
        toast.error(res.message);
      }
    } catch (e) {
      toast.dismiss(t);
      const msg = e instanceof Error ? e.message : String(e);
      setTestResult('failed');
      setTestMessage(msg);
      toast.error('Connection test failed', { description: msg });
    }
  };

  // Complete handler — real POST to tenant_api_connections.
  const createConnection = useCreateApiConnection();
  const apiConnectionsEnabled =
    capabilities.data?.adapters.restApi.enabled === true &&
    capabilities.data?.adapters.graphqlApi.enabled === true;
  const apiUnavailableReason = capabilities.isError
    ? 'Capability check failed. Try again or ask the platform operator to verify Connections settings.'
    : capabilities.isLoading
      ? 'Checking access...'
      : capabilities.data?.adapters.restApi.reason ??
      'External API connections are unavailable in this deployment.';

  if (!apiConnectionsEnabled) {
    return (
      <ConnectionsRoleGate backHref="/connections/api" surface="API connection">
        <div className="flex flex-col h-full">
          <div className="border-b border-border/40 bg-background px-6 py-3 flex items-center gap-3 shrink-0">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate({ to: '/connections/api' })} aria-label="Back to API connections" title="Back">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Separator orientation="vertical" className="h-5" />
            <div>
              <h1 className="text-lg font-semibold">Add API Connection</h1>
              <p className="text-xs text-muted-foreground">External API connections are not available right now</p>
            </div>
          </div>
          <div className="p-6">
            <Card className="max-w-2xl border-amber-500/30 bg-amber-500/5">
              <CardContent className="pt-4 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div className="space-y-2">
                  <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                    API connection creation is disabled for this deployment
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {apiUnavailableReason}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Use an MCP Tier 2 server with an explicit egress allowlist entry when external connectivity is required.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </ConnectionsRoleGate>
    );
  }

  const handleComplete = () => {
    const selectedEndpoints = endpoints.filter(e => e.selected).map(e => ({
      id: e.id,
      method: e.method,
      path: e.path,
      operationId: e.operationId,
      summary: e.summary,
      category: e.category,
      enabled: true,
      callCount: 0,
      avgLatencyMs: 0,
    }));
    createConnection.mutate(
      {
        displayName: connectionName,
        description: connectionDescription,
        baseUrl,
        specFormat,
        authType,
        authConfig: buildAuthConfig(),
        endpoints: selectedEndpoints,
      },
      {
        onSuccess: () => {
          toast.success(`API connection "${connectionName}" created with ${selectedCount} endpoints`);
          navigate({ to: '/connections/api' });
        },
        onError: (e) => toast.error('Create failed', { description: e.message }),
      },
    );
  };

  return (
    <ConnectionsRoleGate backHref="/connections/api" surface="API connection">
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-border/40 bg-background px-6 py-3 flex items-center gap-3 shrink-0">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate({ to: '/connections/api' })} aria-label="Back to API connections" title="Back">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <Separator orientation="vertical" className="h-5" />
        <div>
          <h1 className="text-lg font-semibold">Add API Connection</h1>
          <p className="text-xs text-muted-foreground">Connect a REST or GraphQL API by uploading its specification</p>
        </div>
      </div>

      {/* Wizard */}
      <StepWizard
        steps={[
          {
            id: 'upload',
            title: 'Upload Spec',
            description: 'Paste a JSON OpenAPI 3 specification to discover endpoints. (YAML, Swagger 2, and GraphQL parsing land in v1.2.)',
            canProceed: () => parsed && endpoints.length > 0,
            content: (
              <div className="max-w-3xl space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Specification Format</Label>
                  {/*
                    Codex retest #263 — round 6 caught a UI/parser
                    contract mismatch: the wizard advertised
                    `OpenAPI 3.0 (JSON/YAML)`, `Swagger 2.0
                    (JSON/YAML)`, and `GraphQL Schema`, but the
                    backend `/connections-module/parse-spec` route
                    only accepts `JSON.parse`-able OpenAPI 3.
                    Selecting any other option → admin pastes a
                    valid spec → parse fails with "Spec must be
                    JSON OpenAPI 3." That is a trust gap.

                    Round 6 fix is option (a) from the retest brief
                    — honest UI. We keep the dropdown so admins can
                    see what's coming, but every non-shipping
                    format renders disabled with an inline hint.
                    Selecting one is impossible. The actual
                    parser/dropdown ships its own follow-up issue
                    for the v1.2 epic; round 6 is UI-only.
                  */}
                  <Select value={specFormat} onValueChange={v => setSpecFormat(v as typeof specFormat)}>
                    <SelectTrigger className="w-72">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="openapi3">JSON OpenAPI 3 (active)</SelectItem>
                      <SelectItem value="openapi3_yaml" disabled>
                        YAML OpenAPI 3 — not yet supported (v1.2)
                      </SelectItem>
                      <SelectItem value="openapi2" disabled>
                        Swagger 2.0 — not yet supported (v1.2)
                      </SelectItem>
                      <SelectItem value="graphql" disabled>
                        GraphQL Schema — not yet supported (v1.2)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground">
                    Only JSON OpenAPI 3 is parsed today. YAML, Swagger 2, and GraphQL parsing are tracked for v1.2 — paste a JSON spec for now.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">API Specification (JSON)</Label>
                  <Textarea
                    placeholder={'Paste your JSON OpenAPI 3 specification here...\n\n{\n  "openapi": "3.0.0",\n  "info": { "title": "My API", "version": "1.0" },\n  "paths": { ... }\n}'}
                    value={specText}
                    onChange={e => { setSpecText(e.target.value); setParsed(false); }}
                    rows={12}
                    className="font-mono text-xs"
                  />
                </div>

                <Button onClick={handleParse} disabled={!specText.trim()}>
                  <FileJson className="h-4 w-4 mr-2" /> Parse Specification
                </Button>

                {parsed && (
                  <Card className="border-emerald-500/20 bg-emerald-500/5">
                    <CardContent className="pt-3 pb-2 flex items-center gap-2">
                      <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                      <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                        Discovered {endpoints.length} endpoints
                      </span>
                      <span className="text-xs text-muted-foreground ml-2">
                        ({endpoints.filter(ep => ep.category === 'read').length} read, {endpoints.filter(ep => ep.category === 'write').length} write, {endpoints.filter(ep => ep.category === 'admin').length} admin)
                      </span>
                    </CardContent>
                  </Card>
                )}
              </div>
            ),
          },
          {
            id: 'auth',
            title: 'Configure Auth',
            description: 'Set up authentication for this API connection.',
            // Codex retest #239 — block Next when the chosen auth
            // scheme is missing required credentials. `none` always
            // passes; everything else needs its secret slot filled.
            canProceed: () => authConfigured,
            content: (
              <div className="max-w-xl space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Authentication Type</Label>
                  <Select value={authType} onValueChange={v => setAuthType(v as AuthType)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="api_key">API Key</SelectItem>
                      <SelectItem value="oauth2">OAuth 2.0 (Client Credentials)</SelectItem>
                      <SelectItem value="basic">Basic Authentication</SelectItem>
                      <SelectItem value="bearer">Bearer Token</SelectItem>
                      {/* Codex retest #265 — Custom Headers exposed
                          per the functional spec; backend already
                          handles `custom_headers` in the probe and
                          post-save test paths. */}
                      <SelectItem value="custom_headers">Custom Headers</SelectItem>
                      <SelectItem value="none">No Authentication</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {authType === 'api_key' && (
                  <>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Header Name</Label>
                      <Input value={authApiKeyHeader} onChange={e => setAuthApiKeyHeader(e.target.value)} className="font-mono" placeholder="X-API-Key" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">API Key</Label>
                      <div className="relative">
                        <Input
                          type={showSecret ? 'text' : 'password'}
                          value={authApiKeyValue}
                          onChange={e => setAuthApiKeyValue(e.target.value)}
                          className="font-mono pr-10"
                          placeholder="sk-..."
                        />
                        <Button variant="ghost" size="icon" className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7" onClick={() => setShowSecret(!showSecret)} aria-label={showSecret ? 'Hide secret' : 'Show secret'} title={showSecret ? 'Hide' : 'Show'}>
                          {showSecret ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        </Button>
                      </div>
                    </div>
                  </>
                )}

                {authType === 'oauth2' && (
                  <>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Token URL</Label>
                      <Input value={authOauthTokenUrl} onChange={e => setAuthOauthTokenUrl(e.target.value)} className="font-mono" placeholder="https://auth.example.com/oauth/token" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs">Client ID</Label>
                        <Input value={authOauthClientId} onChange={e => setAuthOauthClientId(e.target.value)} className="font-mono" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Client Secret</Label>
                        <Input type="password" value={authOauthClientSecret} onChange={e => setAuthOauthClientSecret(e.target.value)} className="font-mono" />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Scopes (comma-separated)</Label>
                      <Input value={authOauthScopes} onChange={e => setAuthOauthScopes(e.target.value)} placeholder="read:data, write:orders" />
                    </div>
                  </>
                )}

                {authType === 'basic' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Username</Label>
                      <Input value={authBasicUser} onChange={e => setAuthBasicUser(e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Password</Label>
                      <Input type="password" value={authBasicPass} onChange={e => setAuthBasicPass(e.target.value)} />
                    </div>
                  </div>
                )}

                {authType === 'bearer' && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Bearer Token</Label>
                    <div className="relative">
                      <Input
                        type={showSecret ? 'text' : 'password'}
                        value={authBearerToken}
                        onChange={e => setAuthBearerToken(e.target.value)}
                        className="font-mono pr-10"
                      />
                      <Button variant="ghost" size="icon" className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7" onClick={() => setShowSecret(!showSecret)} aria-label={showSecret ? 'Hide token' : 'Show token'} title={showSecret ? 'Hide' : 'Show'}>
                        {showSecret ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                  </div>
                )}

                {/*
                  Codex retest #265 — Custom Headers editor. Each row
                  is one (Header Name, Header Value) pair. We render
                  the value field as `type="password"` because admins
                  often paste long-lived tokens into custom headers
                  (e.g. `X-Auth-Token`, `X-Api-Secret`); the
                  show/hide toggle on the API-key/Bearer fields is
                  scheme-specific so we keep the masking simple here.
                  Empty rows are tolerated and dropped at submit time.
                */}
                {authType === 'custom_headers' && (
                  <div className="space-y-3">
                    <p className="text-[11px] text-muted-foreground">
                      Each pair is sent as an HTTP header on every call to this connection. Use this when the upstream API expects a non-standard header (e.g. <span className="font-mono">X-Auth-Token</span>). Header values are stored encrypted and redacted from API responses.
                    </p>
                    <div className="space-y-2">
                      {authCustomHeaders.map((h, idx) => (
                        <div key={h.id} className="grid grid-cols-[1fr_1fr_36px] gap-2 items-end">
                          <div className="space-y-1">
                            <Label className="text-[10px] text-muted-foreground">{idx === 0 ? 'Header Name' : ''}</Label>
                            <Input
                              value={h.name}
                              onChange={e =>
                                setAuthCustomHeaders(prev =>
                                  prev.map(p => (p.id === h.id ? { ...p, name: e.target.value } : p)),
                                )
                              }
                              placeholder="X-Auth-Token"
                              className="font-mono"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-[10px] text-muted-foreground">{idx === 0 ? 'Header Value' : ''}</Label>
                            <Input
                              type="password"
                              value={h.value}
                              onChange={e =>
                                setAuthCustomHeaders(prev =>
                                  prev.map(p => (p.id === h.id ? { ...p, value: e.target.value } : p)),
                                )
                              }
                              placeholder="••••••"
                              className="font-mono"
                            />
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 self-end text-muted-foreground hover:text-destructive"
                            onClick={() =>
                              setAuthCustomHeaders(prev =>
                                prev.length > 1
                                  ? prev.filter(p => p.id !== h.id)
                                  : [{ id: h.id, name: '', value: '' }],
                              )
                            }
                            aria-label={`Remove header ${h.name || idx + 1}`}
                            title="Remove header"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={() =>
                        setAuthCustomHeaders(prev => [
                          ...prev,
                          { id: `h-${prev.length + 1}-${Date.now()}`, name: '', value: '' },
                        ])
                      }
                    >
                      <Plus className="h-3.5 w-3.5" /> Add Header
                    </Button>
                  </div>
                )}

                {authType === 'none' && (
                  <Card className="border-amber-500/20 bg-amber-500/5">
                    <CardContent className="pt-3 pb-2 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                      <span className="text-xs text-amber-600 dark:text-amber-400">
                        No authentication configured. Ensure this API does not require credentials.
                      </span>
                    </CardContent>
                  </Card>
                )}
              </div>
            ),
          },
          {
            id: 'endpoints',
            title: 'Select Endpoints',
            description: 'Choose which endpoints to expose as callable tools for agents.',
            canProceed: () => selectedCount > 0,
            content: (
              <div className="max-w-4xl space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">{selectedCount} of {endpoints.length} endpoints selected</p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={selectAll}>Select All</Button>
                    <Button variant="outline" size="sm" onClick={deselectAll}>Deselect All</Button>
                  </div>
                </div>

                <div className="rounded-lg border">
                  <div className="grid grid-cols-[40px_70px_1fr_90px] gap-3 p-3 bg-muted/50 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                    <span></span>
                    <span>Method</span>
                    <span>Endpoint</span>
                    <span>Category</span>
                  </div>
                  <div className="divide-y">
                    {endpoints.map(ep => (
                      <div
                        key={ep.id}
                        className={cn(
                          'grid grid-cols-[40px_70px_1fr_90px] gap-3 p-3 items-center cursor-pointer transition-colors',
                          ep.selected ? 'hover:bg-muted/30' : 'opacity-50 hover:opacity-70'
                        )}
                        onClick={() => toggleEndpoint(ep.id)}
                      >
                        <div className="flex justify-center">
                          <Checkbox checked={ep.selected} onCheckedChange={() => toggleEndpoint(ep.id)} />
                        </div>
                        <Badge variant="outline" className={cn('text-[9px] font-mono w-fit', METHOD_COLORS[ep.method])}>
                          {ep.method}
                        </Badge>
                        <div className="min-w-0">
                          <p className="text-xs font-mono truncate">{ep.path}</p>
                          <p className="text-[11px] text-muted-foreground truncate">{ep.summary}</p>
                        </div>
                        <Badge variant="secondary" className="text-[9px] w-fit capitalize">{ep.category}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ),
          },
          {
            id: 'review',
            title: 'Review & Test',
            description: 'Name your connection, test it, and finalize.',
            canProceed: () => connectionName.trim().length > 0,
            content: (
              <div className="max-w-xl space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Connection Name<span className="text-red-500 ml-0.5">*</span></Label>
                  <Input
                    value={connectionName}
                    onChange={e => setConnectionName(e.target.value)}
                    placeholder="e.g., Acme Maintenance API"
                    maxLength={60}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">Description</Label>
                  <Textarea
                    value={connectionDescription}
                    onChange={e => setConnectionDescription(e.target.value)}
                    placeholder="Brief description..."
                    rows={2}
                    maxLength={200}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">Base URL</Label>
                  <Input
                    value={baseUrl}
                    onChange={e => setBaseUrl(e.target.value)}
                    className="font-mono"
                    placeholder="https://api.example.com/v1"
                  />
                </div>

                <Separator />

                {/* Summary */}
                <Card>
                  <CardContent className="pt-4">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase">Format</p>
                        <p>{specFormat === 'openapi3' ? 'OpenAPI 3.0' : specFormat === 'openapi2' ? 'Swagger 2.0' : 'GraphQL'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase">Auth Type</p>
                        <p>{AUTH_LABELS[authType]}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase">Selected Endpoints</p>
                        <p>{selectedCount} of {endpoints.length}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase">Read / Write / Admin</p>
                        <p>
                          {endpoints.filter(ep => ep.selected && ep.category === 'read').length} / {' '}
                          {endpoints.filter(ep => ep.selected && ep.category === 'write').length} / {' '}
                          {endpoints.filter(ep => ep.selected && ep.category === 'admin').length}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Test connection */}
                <div className="flex items-start gap-3 flex-wrap">
                  <Button
                    variant="outline"
                    onClick={handleTestConnection}
                    disabled={testResult === 'testing' || !baseUrl.trim()}
                    className="gap-1.5"
                  >
                    {testResult === 'testing' ? (
                      <>Testing...</>
                    ) : (
                      <><Key className="h-3.5 w-3.5" /> Test Connection</>
                    )}
                  </Button>
                  {/*
                    Codex retest #239 — show the real probe message
                    (HTTP status + latency or failure reason) instead
                    of an opaque "Connection verified" string. This is
                    the difference between honest verification and a
                    fake-pass against a dead host.

                    Codex retest #264 — when the probe is OAuth, the
                    backend returns a `steps` array with per-step
                    status. Render each step as its own line so the
                    admin sees exactly where OAuth setups break (token
                    endpoint vs. issued token vs. base URL access).
                    For non-OAuth, fall back to the round-5 single
                    line.
                  */}
                  {testResult === 'success' && testMessage && !testSteps && (
                    <span className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 break-all">
                      <Check className="h-3.5 w-3.5 shrink-0" /> {testMessage}
                    </span>
                  )}
                  {testResult === 'failed' && !testSteps && (
                    <span className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400 break-all">
                      <X className="h-3.5 w-3.5 shrink-0" /> {testMessage || 'Connection failed'}
                    </span>
                  )}
                </div>
                {testSteps && testSteps.length > 0 && (
                  <Card className={cn(
                    'mt-3',
                    testResult === 'success'
                      ? 'border-emerald-500/20 bg-emerald-500/5'
                      : 'border-red-500/20 bg-red-500/5',
                  )}>
                    <CardContent className="pt-3 pb-3 space-y-2">
                      <p className="text-xs font-medium">
                        {testResult === 'success' ? 'OAuth probe summary' : 'OAuth probe failed'}
                      </p>
                      <ul className="space-y-1.5">
                        {testSteps.map((step, idx) => (
                          <li key={`${idx}-${step.name}`} className="flex items-start gap-2 text-xs">
                            {step.ok ? (
                              <Check className="h-3.5 w-3.5 shrink-0 mt-0.5 text-emerald-600 dark:text-emerald-400" />
                            ) : (
                              <X className="h-3.5 w-3.5 shrink-0 mt-0.5 text-red-600 dark:text-red-400" />
                            )}
                            <div className="min-w-0">
                              <p className={cn(
                                'font-medium',
                                step.ok
                                  ? 'text-emerald-700 dark:text-emerald-400'
                                  : 'text-red-700 dark:text-red-400',
                              )}>
                                {step.name}
                                {step.latencyMs != null && step.latencyMs > 0 && (
                                  <span className="ml-1 text-muted-foreground font-normal">({step.latencyMs}ms)</span>
                                )}
                              </p>
                              <p className="text-muted-foreground break-all">{step.message}</p>
                            </div>
                          </li>
                        ))}
                      </ul>
                      {testMessage && (
                        <p className="text-[11px] text-muted-foreground pt-1 border-t">
                          {testMessage}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            ),
          },
        ]}
        onComplete={handleComplete}
        onCancel={() => navigate({ to: '/connections/api' })}
        completeLabel="Create Connection"
      />
    </div>
    </ConnectionsRoleGate>
  );
}
